import { CamelCaseKeys } from '@/common/types/snake-to-camel-case';

export const LEDGER_ENTRY_STATUSES = ['NOT_SETTLED', 'SETTLED'] as const;
export type LedgerEntryStatus = (typeof LEDGER_ENTRY_STATUSES)[number];

export const LEDGER_ENTRY_STATUS_LABEL: Record<LedgerEntryStatus, string> = {
	NOT_SETTLED: 'Do rozliczenia',
	SETTLED: 'Rozliczone',
};

export interface DriverLedgerCategoryOptionApiData {
	id: number;
	name: string;
}

export type DriverLedgerCategoryOption = CamelCaseKeys<DriverLedgerCategoryOptionApiData>;

export interface DocumentNumberDuplicate {
	id: number;
	date: string;
}

export interface DocumentNumberCheck {
	duplicate: boolean;
	entry: DocumentNumberDuplicate | null;
}

export interface DriverLedgerEntryLineApiData {
	id: number;
	category_id: number | null;
	category_name: string | null;
	amount: number;
	description: string | null;
	position: number;
}

export type DriverLedgerEntryLine = CamelCaseKeys<DriverLedgerEntryLineApiData>;

export interface DriverLedgerEntryApiData {
	id: number;
	driver_id: string;
	date: string;
	amount: number;
	currency: string;
	amount_pln: number;
	/** Null means the amount was never converted - either it is already PLN, or no rate resolved. */
	exchange_rate: string | null;
	/** NBP quotation day behind `exchange_rate` (D-1 from `date`, or the last quotation before it). */
	exchange_rate_date: string | null;
	amount_eur: number;
	exchange_rate_eur: string | null;
	exchange_rate_eur_date: string | null;
	target_currency: string;
	category_id: number | null;
	category_name: string | null;
	description: string | null;
	document_number: string | null;
	payment_date: string | null;
	status: LedgerEntryStatus;
	/** Row a settlement posted itself - not a real transaction. `system_kind` says which one. */
	is_closing: boolean;
	/**
	 * `closing` zeroes the kasa, `carryover` hands back the part the settlement chose not to deduct
	 * and is the one system row that stays pending. Null for entries people actually made.
	 */
	system_kind: LedgerSystemKind | null;
	carry_from_salary_id: number | null;
	has_original_document: boolean;
	is_mine: boolean;
	created_at: string | null;
	updated_at: string | null;
	created_by: string | null;
	updated_by: string | null;
	vehicle_id: string | null;
	vehicle_name: string | null;
	vehicle_registration_number: string | null;
	commission: number | null;
	lines: DriverLedgerEntryLineApiData[];
}

export type LedgerSystemKind = 'closing' | 'carryover';

export type DriverLedgerEntry = CamelCaseKeys<DriverLedgerEntryApiData>;

export interface DriverLedgerPaginatedApiData {
	data: {
		items: DriverLedgerEntryApiData[];
		total: number;
		page: number;
		per_page: number;
		total_pages: number;
	};
}

export interface DriverLedgerPaginatedResponse {
	items: DriverLedgerEntry[];
	total: number;
	page: number;
	perPage: number;
	totalPages: number;
}

export interface DriverLedgerFilters {
	page?: number;
	perPage?: number;
	dateFrom?: string;
	dateTo?: string;
	currency?: string;
	categoryId?: number;
	status?: LedgerEntryStatus;
	settledBySalaryId?: number;
}

export interface CurrencyBalance {
	currency: string;
	balance: number;
	balancePln: number;
	balanceEur: number;
}

export interface DriverLedgerSummaryApiData {
	data: {
		currencies: { currency: string; balance: number; balance_pln: number; balance_eur: number }[];
		total_pln: number;
		total_eur: number;
	};
}

export interface DriverLedgerSummary {
	currencies: CurrencyBalance[];
	totalPln: number;
	totalEur: number;
}

export interface DriverLedgerPendingSummaryApiData {
	data: {
		count: number;
		total_pln: number;
		total_eur: number;
		total_eur_pln: number;
	};
}

export interface DriverLedgerPendingSummary {
	count: number;
	totalPln: number;
	totalEur: number;
	/** The EUR bucket expressed in PLN, summed per entry at the rate each was booked at. */
	totalEurPln: number;
}

export interface EntryLinePayload {
	category_id: number | null;
	amount: number;
	description: string | null;
}

export interface StoreDriverLedgerPayload {
	date: string;
	amount: number;
	currency: string;
	target_currency: 'PLN' | 'EUR';
	lines: EntryLinePayload[];
	description: string | null;
	document_number: string | null;
	payment_date: string | null;
	vehicle_id: string | null;
	commission: number | null;
	status: LedgerEntryStatus;
	has_original_document: boolean;
}

export interface UpdateDriverLedgerPayload {
	date?: string;
	amount?: number;
	currency?: string;
	target_currency?: 'PLN' | 'EUR';
	lines?: EntryLinePayload[];
	description?: string | null;
	document_number?: string | null;
	payment_date?: string | null;
	vehicle_id?: string | null;
	commission?: number | null;
	status?: LedgerEntryStatus;
	has_original_document?: boolean;
}

export interface VehicleSearchResult {
	id: string;
	name: string;
	registrationNumber: string;
}

export interface DriverLedgerFormDialogData {
	driverId: string;
	entry?: DriverLedgerEntry;
}

export type DriverLedgerFormDialogResult =
	| { action: 'create'; payload: StoreDriverLedgerPayload }
	| { action: 'update'; payload: UpdateDriverLedgerPayload };
